import { Component, OnInit, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { finalize, switchMap } from 'rxjs'
import { BaseComponent } from '../../../../common/components/base.component'
import { MfaService } from '../../../../common/services/mfa/mfa.service'
import { AuthService } from '../../../../common/services/auth.service'
import { getPendingLaunchId } from '../../../../common/util/session-util'

/**
 * Authenticator (TOTP) enrollment. Fetches the secret + otpauth URI, renders a QR for the
 * user to scan (with the secret as a manual-entry fallback), and accepts a code from the user to confirm setup.
 */
@Component({
  selector: 'app-totp-setup',
  templateUrl: './totp-setup.component.html',
  styleUrl: './totp-setup.component.scss',
  standalone: false
})
export class TotpSetupComponent extends BaseComponent implements OnInit {

  #mfaService = inject(MfaService)
  #authService = inject(AuthService)
  #spinnerId = 'mfa-spinner'

  otpAuthUri = ''
  secret = ''
  errorMessage = ''

  codeForm = new FormGroup({
    code: new FormControl('', [Validators.required, Validators.pattern('^[0-9]{6}$')])
  })

  ngOnInit(): void {
    this.spinnerService.setLoading(true, this.#spinnerId)
    this.subscriptions.add(this.#mfaService.totpEnroll()
      .pipe(finalize(() => this.spinnerService.setLoading(false, this.#spinnerId)))
      .subscribe({
        next: (resp) => {
          this.otpAuthUri = resp.OtpAuthUri
          this.secret = resp.Secret
        },
        error: () => {
          this.notificationService.showError('Unable to start authenticator setup. Please try again.')
          this.router.navigate(['/mfa/selection'])
        }
      }))
  }

  onConfirm(): void {
    if (!this.codeForm.valid) {
      return
    }
    this.errorMessage = ''
    this.spinnerService.setLoading(true, this.#spinnerId)

    this.subscriptions.add(this.#mfaService.totpConfirm(String(this.codeForm.value.code))
      .pipe(
        switchMap(() => this.#authService.getWelcomeInfo()),
        finalize(() => this.spinnerService.setLoading(false, this.#spinnerId))
      )
      .subscribe({
        next: () => {
          let route: string = this.#authService.getHomeRoute();
          if (this.featureFlagService.isLICProcessEnabled()) {
            const launchId = getPendingLaunchId();
            if (launchId) {
              route = 'auth/lic'
            }
          }
          this.router.navigate([route])
        },
        error: () => this.errorMessage = 'Invalid code. Please try again.'
      }))
  }

  onBackClicked(): void {
    this.router.navigate(['/mfa/selection'])
  }
}
